import dayjs from 'dayjs';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import DataRow, { DataRowItem } from '@/libs/antd/components/DataRow';
import {
	useQcPdMainDetail,
	useQcPdCustomerDetail,
	useQcPdApplicationDetail,
	useQcPdAssetDetail,
	useQcPdFinancialDetail,
	useQcPdInsuranceDetail,
	useQcPdIncentiveDetail,
	useSaveQcPdCustomer,
	useSaveQcPdApplication,
	useSaveQcPdAsset,
	useSaveQcPdFinancial,
	useSaveQcPdInsurance,
	useSaveQcPdIncentive,
	useSubmitQcPd,
} from '@/libs/fleet-api/services/qc-pra-disbursement';
import { usePrintPurchaseOrder } from '@/libs/fleet-api/hooks/purchase-order';
import { usePreviewAgreementPrintingMutation } from '@/libs/fleet-api/services/print';
import QCTabForm, {
	QC_PRA_PENCAIRAN_TABS,
} from '@/pages/robotic/components/QCTabForm';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Form, Modal, notification, Spin } from 'antd';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '@/App';
import { useForm } from '@/libs/antd/form';
import { useModals } from '@/components/modals/ModalProvider';
import { FleetFile } from '@/libs/fleet-api/fetcher';
import {
	mapMainDetailToInfo,
	mapCustomerDetailToForm,
	mapApplicationDetailToForm,
	mapAssetDetailToForm,
	mapFinancialDetailToForm,
	mapInsuranceDetailToForm,
	mapIncentiveDetailToForm,
	mapFormToSaveCustomer,
	mapFormToSaveApplication,
	mapFormToSaveAsset,
	mapFormToSaveFinancial,
	mapFormToSaveInsurance,
	mapFormToSaveIncentive,
	TAB_FIELD_NAMES,
} from './qc-pd-mapping';
import {
	QCPraPencairanContext,
	type QCPraPencairanContextValue,
} from './QCPraPencairanContext';

type QCPraPencairanPageProps = {
	purchaseOrderNo: string;
	mode: 'edit' | 'view';
	onBack?: () => void;
};

const TAB_KEYS = [
	'data-customer',
	'application-data',
	'asset-data',
	'insurance-data',
	'financial-data',
] as const;

const QCPraPencairanPage: FC<QCPraPencairanPageProps> = ({
	purchaseOrderNo,
	mode,
	onBack,
}) => {
	const { navigate } = useApp();
	const { form } = useForm();
	const {
		openCustomerGroupDetail,
		openPreviewModal,
		openCustomerDetail,
		openDocumentModal,
	} = useModals();
	const { mutateAsync: printPurchaseOrderMutate } = usePrintPurchaseOrder();
	const { mutateAsync: previewAgreementMutate } = usePreviewAgreementPrintingMutation();

	const [activeTab, setActiveTab] = useState<string>('data-customer');
	const [savedTabs, setSavedTabs] = useState<Set<string>>(new Set());
	const [dirtyTabs, setDirtyTabs] = useState<Set<string>>(new Set());
	const debugFieldMeta = __IS_DEVELOPMENT__ && new URLSearchParams(window.location.search).get('debug') === 'true';
	const pendingTabRef = useRef<string | null>(null);

	const [apiData, setApiData] = useState<QCPraPencairanContextValue>({
		fieldMeta: {},
		saham: { data: [], meta: [] },
		asset: {
			assetMeta: null,
			customerAddress: {
				alamat: '',
				rt: '',
				rw: '',
				kodePos: '',
				kelurahan: '',
				kecamatan: '',
				kota: '',
			},
		},
		insurance: {
			paymentSchedule: [],
			tenorYears: [],
			paymentDocumentUrl: '',
			policyDocumentUrl: '',
		},
		incentive: {
			distributionData: [],
			showroomEmployeeOptions: {},
			mandiriEmployeeOptions: {},
			documentUploadUrl: '',
		},
	});

	// ── Per-tab queries ──────────────────────────────────────────────────
	const mainQuery = useQcPdMainDetail({ purchaseOrderNo });
	const customerQuery = useQcPdCustomerDetail({ purchaseOrderNo });
	const applicationQuery = useQcPdApplicationDetail({ purchaseOrderNo });
	const assetQuery = useQcPdAssetDetail({ purchaseOrderNo });
	const financialQuery = useQcPdFinancialDetail({ purchaseOrderNo });
	const insuranceQuery = useQcPdInsuranceDetail({ purchaseOrderNo });
	const incentiveQuery = useQcPdIncentiveDetail({ purchaseOrderNo });

	const isLoading = mainQuery.isLoading;

	// ── Save mutations ───────────────────────────────────────────────────
	const saveCustomerMutation = useSaveQcPdCustomer();
	const saveApplicationMutation = useSaveQcPdApplication();
	const saveAssetMutation = useSaveQcPdAsset();
	const saveFinancialMutation = useSaveQcPdFinancial();
	const saveInsuranceMutation = useSaveQcPdInsurance();
	const saveIncentiveMutation = useSaveQcPdIncentive();
	const submitMutation = useSubmitQcPd();

	const isReadOnly = mode === 'view';
	const isSaving =
		saveCustomerMutation.isPending ||
		saveApplicationMutation.isPending ||
		saveAssetMutation.isPending ||
		saveFinancialMutation.isPending ||
		saveInsuranceMutation.isPending ||
		saveIncentiveMutation.isPending;
	const isSubmitting = submitMutation.isPending;
	const allTabsSaved = TAB_KEYS.every((k) => savedTabs.has(k));

	// ── Map API data to form ─────────────────────────────────────────────
	const mainInfo = useMemo(() => {
		if (!mainQuery.data?.data) return null;
		return mapMainDetailToInfo(mainQuery.data.data);
	}, [mainQuery.data]);

	useEffect(() => {
		if (customerQuery.data?.data) {
			const result = mapCustomerDetailToForm(customerQuery.data.data);
			form.setFieldsValue(result.values);
			const sahamData =
				(result.values.sahamData as Record<string, unknown>[]) ?? [];
			const sahamMeta =
				(result.values
					.sahamDataMeta as import('./qc-pd-mapping').FieldMetaMap[]) ?? [];
			setApiData((prev) => ({
				...prev,
				fieldMeta: { ...prev.fieldMeta, ...result.fieldMeta },
				saham: { data: sahamData, meta: sahamMeta },
			}));
		}
	}, [customerQuery.data, form]);

	useEffect(() => {
		if (applicationQuery.data?.data) {
			const result = mapApplicationDetailToForm(applicationQuery.data.data);
			form.setFieldsValue({
				...result.values,
				pemilikManfaatData: result.beneficialOwnersData,
			});
			setApiData((prev) => ({
				...prev,
				fieldMeta: { ...prev.fieldMeta, ...result.fieldMeta },
				beneficialOwnerOptions: result.beneficialOwnerOptions,
			}));
		}
	}, [applicationQuery.data, form]);

	useEffect(() => {
		if (assetQuery.data?.data) {
			const result = mapAssetDetailToForm(assetQuery.data.data);
			form.setFieldsValue(result.values);
			setApiData((prev) => ({
				...prev,
				fieldMeta: { ...prev.fieldMeta, ...result.fieldMeta },
				asset: {
					assetMeta: result.assetMeta,
					customerAddress: result.customerAddress,
				},
			}));
		}
	}, [assetQuery.data, form]);

	useEffect(() => {
		if (financialQuery.data?.data) {
			const result = mapFinancialDetailToForm(financialQuery.data.data);
			form.setFieldsValue(result.values);
			setApiData((prev) => ({
				...prev,
				fieldMeta: { ...prev.fieldMeta, ...result.fieldMeta },
			}));
		}
	}, [financialQuery.data, form]);

	useEffect(() => {
		if (insuranceQuery.data?.data) {
			const result = mapInsuranceDetailToForm(insuranceQuery.data.data);
			form.setFieldsValue(result.values);
			setApiData((prev) => ({
				...prev,
				fieldMeta: { ...prev.fieldMeta, ...result.fieldMeta },
				insurance: {
					paymentSchedule: result.paymentSchedule,
					tenorYears: result.tenorYears,
					paymentDocumentUrl: result.paymentDocumentUrl,
					policyDocumentUrl: result.policyDocumentUrl,
				},
			}));
		}
	}, [insuranceQuery.data, form]);

	useEffect(() => {
		if (incentiveQuery.data?.data) {
			const result = mapIncentiveDetailToForm(incentiveQuery.data.data);
			form.setFieldsValue(result.values);
			setApiData((prev) => ({
				...prev,
				fieldMeta: { ...prev.fieldMeta, ...result.fieldMeta },
				incentive: {
					distributionData: result.distributionData,
					showroomEmployeeOptions: result.showroomEmployeeOptions,
					mandiriEmployeeOptions: result.mandiriEmployeeOptions,
					documentUploadUrl: result.documentUploadUrl,
				},
			}));
		}
	}, [incentiveQuery.data, form]);

	// ── Dirty tracking via form change ───────────────────────────────────
	const handleValuesChange = useCallback(() => {
		setDirtyTabs((prev) => {
			const next = new Set(prev);
			next.add(activeTab);
			return next;
		});
	}, [activeTab]);

	// ── Tab change with unsaved warning ──────────────────────────────────
	const handleTabChange = useCallback(
		(newTab: string) => {
			if (
				dirtyTabs.has(activeTab) &&
				!savedTabs.has(activeTab) &&
				mode === 'edit'
			) {
				pendingTabRef.current = newTab;
				Modal.confirm({
					title: 'Data Belum Disimpan',
					content:
						'Perubahan pada tab ini belum disimpan. Jika Anda pindah tab, data yang belum disimpan akan hilang. Lanjutkan?',
					okText: 'Ya, Pindah',
					cancelText: 'Batal',
					onOk: () => {
						setDirtyTabs((prev) => {
							const next = new Set(prev);
							next.delete(activeTab);
							return next;
						});
						setActiveTab(newTab);
						pendingTabRef.current = null;
					},
					onCancel: () => {
						pendingTabRef.current = null;
					},
				});
			} else {
				setActiveTab(newTab);
			}
		},
		[activeTab, dirtyTabs, savedTabs, mode],
	);

	// ── Navigation ───────────────────────────────────────────────────────
	const handleBack = () => {
		if (typeof onBack === 'function') {
			onBack();
		} else {
			navigate('QCPraPencairanListPage');
		}
	};

	// ── Save Draft (active tab only) ─────────────────────────────────────
	const handleSaveDraft = async () => {
		const values = form.getFieldsValue() as Record<string, unknown>;

		// Validate mandatory fields for the active tab
		const tabFields = TAB_FIELD_NAMES[activeTab];
		if (tabFields?.length) {
			try {
				await form.validateFields(tabFields);
			} catch (err) {
				const errorFields =
					(
						err as {
							errorFields?: { name: (string | number)[]; errors: string[] }[];
						}
					)?.errorFields ?? [];
				const fieldNames = errorFields.map((f) => f.name.join('.')).slice(0, 5);
				notification.warning({
					message: 'Validasi Gagal',
					description: fieldNames.length
						? `Mohon lengkapi field berikut: ${fieldNames.join(', ')}${errorFields.length > 5 ? `, dan ${errorFields.length - 5} lainnya` : ''}.`
						: 'Mohon lengkapi data yang wajib diisi pada tab ini.',
				});
				return;
			}
		}

		const tabLabel =
			QC_PRA_PENCAIRAN_TABS.find((t) => t.key === activeTab)?.label ??
			activeTab;

		const onSuccess = () => {
			const newSavedTabs = new Set(savedTabs).add(activeTab);
			setSavedTabs(newSavedTabs);
			setDirtyTabs((prev) => {
				const next = new Set(prev);
				next.delete(activeTab);
				return next;
			});
			notification.success({
				message: 'Draft Tersimpan',
				description: `Data tab "${tabLabel}" berhasil disimpan.`,
			});

			// Auto-advance to next tab if available
			const currentIndex = TAB_KEYS.indexOf(
				activeTab as (typeof TAB_KEYS)[number],
			);
			if (currentIndex >= 0 && currentIndex < TAB_KEYS.length - 1) {
				setActiveTab(TAB_KEYS[currentIndex + 1]);
				return;
			}

			// Last tab saved — auto-fire final submit when all tabs are saved
			const allSaved = TAB_KEYS.every((k) => newSavedTabs.has(k));
			if (allSaved) {
				submitMutation.mutate(
					{ purchaseOrderNo },
					{
						onSuccess: () => {
							notification.success({
								message: 'Berhasil Diproses',
								description:
									'Data QC Pra Pencairan berhasil diproses ke Robotic.',
							});
							handleBack();
						},
					},
				);
			}
		};

		const onError = (error: unknown) => {
			const message =
				(error as { message?: string })?.message ??
				`Gagal menyimpan tab "${tabLabel}". Silakan coba lagi.`;
			notification.error({
				message: 'Gagal Menyimpan Draft',
				description: message,
			});
		};

		switch (activeTab) {
			case 'data-customer':
				saveCustomerMutation.mutate(
					mapFormToSaveCustomer(
						purchaseOrderNo,
						mainInfo?.customerId ?? '',
						values,
						1,
					),
					{ onSuccess, onError },
				);
				break;
			case 'application-data':
				saveApplicationMutation.mutate(
					mapFormToSaveApplication(purchaseOrderNo, values, 1),
					{ onSuccess, onError },
				);
				break;
			case 'asset-data':
				saveAssetMutation.mutate(mapFormToSaveAsset(purchaseOrderNo, values), {
					onSuccess,
					onError,
				});
				break;
			case 'financial-data':
				saveFinancialMutation.mutate(
					mapFormToSaveFinancial(purchaseOrderNo, values),
					{ onSuccess, onError },
				);
				break;
			case 'insurance-data':
				saveInsuranceMutation.mutate(
					mapFormToSaveInsurance(purchaseOrderNo, values),
					{ onSuccess, onError },
				);
				break;
			case 'incentive-data':
				saveIncentiveMutation.mutate(
					mapFormToSaveIncentive(
						purchaseOrderNo,
						values,
						apiData.incentive.distributionData,
					),
					{ onSuccess, onError },
				);
				break;
		}
	};

	// ── Submit (Process Robotic) ─────────────────────────────────────────
	const handleSubmit = () => {
		if (!allTabsSaved) {
			const unsaved = TAB_KEYS.filter((k) => !savedTabs.has(k));
			const unsavedLabels = unsaved.map(
				(k) => QC_PRA_PENCAIRAN_TABS.find((t) => t.key === k)?.label ?? k,
			);
			notification.warning({
				message: 'Belum Semua Tab Disimpan',
				description: `Tab berikut belum disimpan: ${unsavedLabels.join(', ')}. Mohon simpan semua tab sebelum proses.`,
			});
			return;
		}

		submitMutation.mutate(
			{ purchaseOrderNo },
			{
				onSuccess: () => {
					notification.success({
						message: 'Berhasil Diproses',
						description: 'Data QC Pra Pencairan berhasil diproses ke Robotic.',
					});
					handleBack();
				},
			},
		);
	};

	const navigateBackToCurrentPage = () => {
		navigate('QCPraPencairanPage' as never, { purchaseOrderNo, mode } as never);
	};

	const renderLink = (label: string, canOpen: boolean, onClick: () => void) => {
		if (!label) return '-';
		if (!canOpen) return label;
		return (
			<a
				href="#"
				className="trigger-popup"
				onClick={(e) => {
					e.preventDefault();
					onClick();
				}}
			>
				{label}
			</a>
		);
	};

	const openSummaryDocument = () => {
		const document = mainInfo?.resolutionSummaryDocument;
		if (!document) return;
		openPreviewModal({
			id: document.id ?? '',
			name: document.name ?? 'Summary Risalah',
			path: document.path ?? undefined,
			url: document.url ?? undefined,
			format: document.format as FleetFile['format'],
		});
	};

	if (isLoading) {
		return (
			<div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
				<Spin size="large" />
			</div>
		);
	}

	const infoItems: DataRowItem[] = [
		[
			[
				'Nama Customer',
				renderLink(
					mainInfo?.customerName ?? '',
					!!mainInfo?.customerId,
					() => openCustomerDetail(mainInfo!.customerId!),
				),
			],
			[
				'No PO',
				renderLink(
					mainInfo?.purchaseOrderNo ?? '',
					!!mainInfo?.purchaseOrderId,
					async () => {
						try {
							const response = await printPurchaseOrderMutate({
								purchaseOrderIds: [mainInfo!.purchaseOrderId!],
							});
							openDocumentModal(response);
						} catch (error) {
							console.error('Failed to load PO document:', error);
						}
					},
				),
			],
		],
		[
			['FRM', mainInfo?.frmName || '-'],
			[
				'Summary Risalah',
				renderLink(
					mainInfo?.resolutionSummaryDocument ? 'View Dokumen' : '',
					!!mainInfo?.resolutionSummaryDocument,
					openSummaryDocument,
				),
			],
		],
		[
			['FBDH', mainInfo?.fbdhName || '-'],
			[
				'No NAP',
				renderLink(mainInfo?.napNo ?? '', !!mainInfo?.prospectId, () =>
					navigate(
						'NAPPage' as never,
						{
							prospectId: mainInfo?.prospectId,
							onBack: navigateBackToCurrentPage,
						} as never,
					),
				),
			],
		],
		[
			['Sumber Order', mainInfo?.sourceOrder || '-'],
			[
				'No SPF',
				renderLink(
					mainInfo?.spfNo ?? '',
					mainInfo?.spfId != null && mainInfo?.napResolutionId != null,
					() =>
						navigate(
							'SPFPage' as never,
							{
								spfId: mainInfo?.spfId,
								napResolutionId: mainInfo?.napResolutionId,
								viewOnly: true,
								onBack: navigateBackToCurrentPage,
							} as never,
						),
				),
			],
		],
		[
			['Jenis Fasilitas', mainInfo?.facilityType || '-'],
			[
				'No MKL',
				renderLink(mainInfo?.mklNo ?? '', mainInfo?.spfId != null, () =>
					navigate(
						'MKLPage' as never,
						{
							mklId: mainInfo?.spfId,
							onBack: navigateBackToCurrentPage,
						} as never,
					),
				),
			],
		],
		[
			['Asset', mainInfo?.asset || '-'],
			[
				'No Simulasi',
				renderLink(
					mainInfo?.simulationNo ?? '',
					mainInfo?.simulationId != null,
					() =>
						navigate(
							'SimulationPage' as never,
							{
								simulationId: mainInfo?.simulationId,
								viewOnly: true,
								showResult: true,
								onBack: navigateBackToCurrentPage,
							} as never,
						),
				),
			],
		],
		[
			[
				'Tanggal RKP',
				mainInfo?.rkpDate ? dayjs(mainInfo.rkpDate).format('DD/MM/YYYY') : '-',
			],
			[
				'No MPP',
				renderLink(mainInfo?.mppNo ?? '', mainInfo?.mppId != null, () =>
					navigate(
						'MPPPage' as never,
						{
							mppId: mainInfo?.mppId,
							viewOnly: true,
							onBack: navigateBackToCurrentPage,
						} as never,
					),
				),
			],
		],
		[
			[
				'Tanggal Risalah Full Approved',
				mainInfo?.resolutionFullApprovedDate
					? dayjs(mainInfo.resolutionFullApprovedDate).format('DD/MM/YYYY')
					: '-',
			],
			[
				'No Perjanjian Pembiayaan',
				renderLink(
					mainInfo?.financingAgreementNo ?? '',
					mainInfo?.agreementPrintingId != null,
					async () => {
						try {
							const response = await previewAgreementMutate({
								id: mainInfo!.agreementPrintingId!,
							});
							const signedUrl = response.data?.signedUrl;
							if (!signedUrl) return;
							openPreviewModal({
								id: String(mainInfo!.agreementPrintingId!),
								name: `Perjanjian Pembiayaan ${mainInfo?.financingAgreementNo ?? ''}`.trim(),
								url: signedUrl,
								format: 'pdf' as FleetFile['format'],
							});
						} catch (error) {
							console.error('Failed to load agreement document:', error);
						}
					},
				),
			],
		],
		[
			[
				'Group BMPP',
				mainInfo?.groupBmppName ? (
					<a
						href="#"
						className="trigger-popup"
						onClick={(e) => {
							e.preventDefault();
							openCustomerGroupDetail(mainInfo?.groupBmppId ?? '');
						}}
					>
						{mainInfo.groupBmppName}
					</a>
				) : (
					'-'
				),
			],
			[
				'No DKRPP',
				renderLink(
					mainInfo?.disbursementNo ?? '',
					!!(
						mainInfo?.mppId &&
						mainInfo?.financingProductId &&
						mainInfo?.purchaseOrderId
					),
					() =>
						navigate(
							'DisbursementPage' as never,
							{
								mppId: mainInfo!.mppId!,
								purchaseOrderIds: [mainInfo!.purchaseOrderId!],
								financingProductId: mainInfo!.financingProductId!,
								viewOnly: true,
								onBack: navigateBackToCurrentPage,
							} as never,
						),
				),
			],
		],
	];

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
			{/* Header */}
			<div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
				<Button
					onClick={handleBack}
					icon={<ArrowLeftOutlined />}
					style={{
						width: 44,
						height: 44,
						background: '#1557BC',
						color: '#fff',
					}}
				/>
				<FleetTitle
					marginBottom={0}
					style={{ flex: 1 }}
				>
					Main Information
				</FleetTitle>
			</div>

			{/* Main Information */}
			<DataRow
				items={infoItems}
				loading={isLoading}
			/>

			{/* Main Data */}
			<FleetTitle
				level={2}
				marginBottom={0}
			>
				Main Data
			</FleetTitle>

	<QCPraPencairanContext.Provider
				value={{
					...apiData,
					debugFieldMeta,
					updateSahamRow: (index, row) => {
						setApiData((prev) => {
							const nextData = [...prev.saham.data];
							nextData[index] = row;
							return { ...prev, saham: { ...prev.saham, data: nextData } };
						});
						const formData =
							(form.getFieldValue('sahamData') as
								| Record<string, unknown>[]
								| undefined) ?? [];
						const nextFormData = [...formData];
						nextFormData[index] = row;
						form.setFieldValue('sahamData', nextFormData);
					},
				}}
			>
				<Form
					{...ANTD_DEFAULT_PROPS.FORM}
					form={form}
					labelCol={{ span: 12 }}
					onValuesChange={handleValuesChange}
					initialValues={{
						setujuPemasaranPihakKetiga: 'no',
						pernahKreditMobil: 'no',
						daftarHitamAppi: 'no',
						setujuPemrosesan: 'no',
						setujuPemasaranMtf: 'no',
					}}
				>
					<QCTabForm
						tabs={QC_PRA_PENCAIRAN_TABS}
						form={form}
						readOnly={isReadOnly}
						activeTab={activeTab}
						onTabChange={handleTabChange}
					/>
				</Form>
			</QCPraPencairanContext.Provider>

			{/* Footer Buttons */}
			<div
				style={{
					display: 'flex',
					gap: 10,
					justifyContent: 'flex-end',
					marginTop: 30,
				}}
			>
				<Button onClick={handleBack}>KEMBALI</Button>

				{mode === 'edit' && (
					<>
						<Button
							type="primary"
							loading={isSaving}
							disabled={isSubmitting}
							onClick={handleSaveDraft}
						>
							SAVE DRAFT
						</Button>
						<Button
							type="primary"
							loading={isSubmitting}
							disabled={!allTabsSaved || isSaving}
							onClick={handleSubmit}
						>
							PROSES ROBOTIC
						</Button>
					</>
				)}
			</div>
		</div>
	);
};

export default QCPraPencairanPage;
